import { formatEur } from '../store';
import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useTheme } from '../ThemeContext';

/* ══════════════════════════════════════════════════════
   Tokens de tema — claro (igual que siempre) / oscuro (estética web)
══════════════════════════════════════════════════════ */
export const THEME = {
  light: {
    bg:'#f0f2f5', surface:'#fff', surface2:'#f9fafb', border:'#e5e7eb', borderStrong:'#d1d5db',
    ink:'#111827', body:'#374151', muted:'#6b7280', faint:'#9ca3af',
    primary:'#1a56db', primaryBg:'#e8f0fe',
  },
  dark: {
    bg:'#0b0b09', surface:'#141412', surface2:'#1c1c18', border:'#2a2a26', borderStrong:'#38382f',
    ink:'#f2f2ee', body:'#d4d4c8', muted:'#8f8f86', faint:'#6f6f66',
    primary:'#a3e635', primaryBg:'rgba(163,230,53,0.14)',
  },
};
export function useT() {
  const { isDark } = useTheme();
  return THEME[isDark ? 'dark' : 'light'];
}

/* Botones fantasma de color (WA/email/PDF/impresión) — pastel en claro, tenue en oscuro */
const PASTEL_BTN = {
  green:  { light:{bg:'#f0fdf4',fg:'#15803d',bd:'#bbf7d0'}, dark:{bg:'rgba(16,185,129,0.16)',fg:'#6ee7b7',bd:'rgba(16,185,129,0.35)'} },
  blue:   { light:{bg:'#eff6ff',fg:'#1d4ed8',bd:'#bfdbfe'}, dark:{bg:'rgba(59,130,246,0.16)',fg:'#93c5fd',bd:'rgba(59,130,246,0.35)'} },
  purple: { light:{bg:'#f5f3ff',fg:'#6d28d9',bd:'#ddd6fe'}, dark:{bg:'rgba(167,139,250,0.18)',fg:'#c4b5fd',bd:'rgba(167,139,250,0.35)'} },
  orange: { light:{bg:'#fff7ed',fg:'#c2410c',bd:'#fed7aa'}, dark:{bg:'rgba(249,115,22,0.16)',fg:'#fdba74',bd:'rgba(249,115,22,0.35)'} },
  amber:  { light:{bg:'#fef9c3',fg:'#854d0e',bd:'#fde68a'}, dark:{bg:'rgba(245,158,11,0.16)',fg:'#fbbf24',bd:'rgba(245,158,11,0.35)'} },
  red:    { light:{bg:'#fee2e2',fg:'#dc2626',bd:'#fecaca'}, dark:{bg:'rgba(239,68,68,0.16)',fg:'#fca5a5',bd:'rgba(239,68,68,0.35)'} },
};
export function pastelBtn(theme, color) {
  const c = PASTEL_BTN[color][theme === THEME.dark ? 'dark' : 'light'];
  return { background:c.bg, color:c.fg, border:`1px solid ${c.bd}` };
}

/* ══════════════════════════════════════════════════════
   useSortable — hook de ordenación para tablas
══════════════════════════════════════════════════════ */
export function useSortable(items, defaultField = null, defaultDir = 'asc') {
  const [sortField, setSortField] = useState(defaultField);
  const [sortDir,   setSortDir]   = useState(defaultDir);

  const handleSort = useCallback((field) => {
    if (!field) return;
    setSortField(prev => {
      if (prev === field) { setSortDir(d => d === 'asc' ? 'desc' : 'asc'); return prev; }
      setSortDir('asc');
      return field;
    });
  }, []);

  const sorted = useMemo(() => (
    sortField ? [...items].sort((a, b) => {
      const av = a[sortField] ?? '';
      const bv = b[sortField] ?? '';
      const cmp = typeof av === 'number' && typeof bv === 'number'
        ? av - bv
        : String(av).localeCompare(String(bv), 'es', { numeric: true });
      return sortDir === 'asc' ? cmp : -cmp;
    }) : items
  ), [items, sortField, sortDir]);

  return { sorted, sortField, sortDir, handleSort };
}

/* ══════════════════════════════════════════════════════
   SortableTh — cabecera de tabla con indicador de orden
══════════════════════════════════════════════════════ */
export function SortableTh({ label, field, sortField, sortDir, onSort, style = {} }) {
  const t = useT();
  const active = field && sortField === field;
  return (
    <th onClick={() => onSort(field)} style={{
      padding:'10px 14px', textAlign:'left', color: active ? t.primary : t.muted,
      fontSize:11, fontWeight:600, borderBottom:`1px solid ${t.border}`,
      cursor: field ? 'pointer' : 'default', userSelect:'none',
      whiteSpace:'nowrap', ...style
    }}>
      {label}
      {field && (
        <span style={{ marginLeft:4, fontSize:10, color: active ? t.primary : t.borderStrong }}>
          {active ? (sortDir === 'asc' ? '▲' : '▼') : '⇅'}
        </span>
      )}
    </th>
  );
}

const s = {
  btn: { display:'inline-flex', alignItems:'center', gap:6, padding:'6px 14px', borderRadius:8, border:'none', fontSize:13, fontWeight:500, cursor:'pointer', fontFamily:'inherit', transition:'opacity .15s, background .15s' },
};

export function Btn({ variant='primary', size='md', onClick, children, style, disabled, type='button', title }) {
  const t = useT();
  const colors = {
    primary: { background:t.primary, color: t===THEME.dark ? '#0b0b09' : '#fff' },
    success: { background:'#0e9f6e', color:'#fff' },
    danger:  { background:'#e02424', color:'#fff' },
    ghost:   { background:t.surface2, color:t.body, border:`1px solid ${t.border}` },
    outline: { background:'transparent', color:t.body, border:`1px solid ${t.borderStrong}` },
    warn:    { background:'#c27803', color:'#fff' },
  };
  const sizes = { sm: { padding:'3px 10px', fontSize:12 }, md: {}, lg: { padding:'9px 18px', fontSize:14 } };
  return (
    <button type={type} disabled={disabled} onClick={onClick} title={title}
      style={{ ...s.btn, ...colors[variant], ...sizes[size], opacity: disabled ? .5 : 1, ...style }}>
      {children}
    </button>
  );
}

export function Badge({ variant='gray', children }) {
  const t = useT();
  const dark = t === THEME.dark;
  const colors = dark ? {
    green:  { background:'rgba(16,185,129,0.16)', color:'#6ee7b7' },
    amber:  { background:'rgba(245,158,11,0.16)', color:'#fbbf24' },
    blue:   { background:'rgba(59,130,246,0.16)', color:'#93c5fd' },
    red:    { background:'rgba(239,68,68,0.16)', color:'#fca5a5' },
    gray:   { background:t.surface2, color:t.muted },
    purple: { background:'rgba(167,139,250,0.18)', color:'#c4b5fd' },
  } : {
    green:  { background:'#d1fae5', color:'#065f46' },
    amber:  { background:'#fef3c7', color:'#92400e' },
    blue:   { background:'#dbeafe', color:'#1e40af' },
    red:    { background:'#fee2e2', color:'#991b1b' },
    gray:   { background:t.surface2, color:t.muted },
    purple: { background:'#ede9fe', color:'#5b21b6' },
  };
  return (
    <span style={{ ...colors[variant], padding:'2px 8px', borderRadius:20, fontSize:11, fontWeight:600, display:'inline-block' }}>
      {children}
    </span>
  );
}

export function Card({ children, style, title }) {
  const t = useT();
  return (
    <div style={{ background:t.surface, borderRadius:12, border:`1px solid ${t.border}`, padding:'16px 18px', ...style }}>
      {title && <div style={{ fontSize:13, fontWeight:600, color:t.body, marginBottom:12 }}>{title}</div>}
      {children}
    </div>
  );
}

export function Input({ label, value, onChange, type='text', placeholder, readOnly, style, min, max, step }) {
  const t = useT();
  return (
    <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
      {label && <label style={{ fontSize:11, fontWeight:600, color:t.muted, textTransform:'uppercase', letterSpacing:'.04em' }}>{label}</label>}
      <input value={value ?? ''} onChange={e => onChange && onChange(e.target.value)} type={type}
        placeholder={placeholder} readOnly={readOnly} min={min} max={max} step={step}
        style={{ padding:'7px 10px', border:`1px solid ${t.borderStrong}`, borderRadius:8, fontSize:13, color:t.ink, background: readOnly ? t.surface2 : t.surface, outline:'none', ...style }} />
    </div>
  );
}

export function Select({ label, value, onChange, options, style, sorted=true }) {
  const t = useT();
  const opts = sorted
    ? [...options].sort((a, b) => (a.label ?? a).toString().localeCompare((b.label ?? b).toString(), 'es'))
    : options;
  return (
    <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
      {label && <label style={{ fontSize:11, fontWeight:600, color:t.muted, textTransform:'uppercase', letterSpacing:'.04em' }}>{label}</label>}
      <select value={value ?? ''} onChange={e => onChange(e.target.value)}
        style={{ padding:'7px 10px', border:`1px solid ${t.borderStrong}`, borderRadius:8, fontSize:13, color:t.ink, background:t.surface, outline:'none', ...style }}>
        {opts.map(o => <option key={o.value ?? o} value={o.value ?? o}>{o.label ?? o}</option>)}
      </select>
    </div>
  );
}

export function Textarea({ label, value, onChange, placeholder, rows=3 }) {
  const t = useT();
  return (
    <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
      {label && <label style={{ fontSize:11, fontWeight:600, color:t.muted, textTransform:'uppercase', letterSpacing:'.04em' }}>{label}</label>}
      <textarea value={value ?? ''} onChange={e => onChange(e.target.value)} rows={rows} placeholder={placeholder}
        style={{ padding:'7px 10px', border:`1px solid ${t.borderStrong}`, borderRadius:8, fontSize:13, color:t.ink, background:t.surface, resize:'vertical', outline:'none', fontFamily:'inherit' }} />
    </div>
  );
}

export function Modal({ title, onClose, children, width=520, height }) {
  const t = useT();
  useEffect(() => {
    const prev = parseInt(document.body.dataset.modals || '0', 10);
    document.body.dataset.modals = prev + 1;
    return () => { document.body.dataset.modals = Math.max(0, parseInt(document.body.dataset.modals || '1', 10) - 1); };
  }, []);
  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.45)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000, padding:16 }}>
      <div style={{ background:t.surface, borderRadius:14, width, maxWidth:'100%', maxHeight:'90vh', overflow:'auto', boxShadow:'0 20px 60px rgba(0,0,0,.4)', ...(height ? { height, display:'flex', flexDirection:'column' } : {}) }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'14px 18px', borderBottom:`1px solid ${t.border}`, position:'sticky', top:0, background:t.surface, zIndex:1, flexShrink:0 }}>
          <span style={{ fontSize:15, fontWeight:600, color:t.ink }}>{title}</span>
          <button onClick={onClose} style={{ background:'none', border:'none', fontSize:20, color:t.muted, cursor:'pointer', lineHeight:1 }}>×</button>
        </div>
        <div style={{ padding:'16px 18px', ...(height ? { flex:1, overflow:'hidden', display:'flex', flexDirection:'column' } : {}) }}>{children}</div>
      </div>
    </div>
  );
}

export function Tfoot({ base, iva, total }) {
  const t = useT();
  return (
    <div style={{ display:'flex', flexDirection:'column', alignItems:'flex-end', gap:4, paddingTop:8, borderTop:`1px solid ${t.border}`, marginTop:4 }}>
      <div style={{ fontSize:12, color:t.muted }}>Base imponible: <b style={{ color:t.ink }}>{formatEur(base)}</b></div>
      <div style={{ fontSize:12, color:t.muted }}>IVA: <b style={{ color:t.ink }}>{formatEur(iva)}</b></div>
      <div style={{ fontSize:15, fontWeight:700, color:t.primary }}>TOTAL: {formatEur(total)}</div>
    </div>
  );
}

const BLOQUEO_ESCANEO_MS = 500;

export function LineasEditor({ lineas, onChange, articulos }) {
  const t = useT();
  const [buscadorIdx, setBuscadorIdx] = useState(null);
  const [buscadorQ, setBuscadorQ] = useState('');
  const [filaEscaneada, setFilaEscaneada] = useState(null);
  const ultimoEscaneo = useRef(0);
  const add = () => onChange([...lineas, { desc:'', cant:1, precio:0, iva:21 }]);
  const upd = (i, f, v) => {
    const next = lineas.map((l, idx) => {
      if (idx !== i) return l;
      if (f === 'ivaIncl') return { ...l, ivaIncl: v };
      if (f === 'iva') {
        const newIva = parseFloat(v) || 0;
        if (l.ivaIncl) {
          const bruto = l.precio * (1 + (l.iva||0)/100);
          return { ...l, iva: newIva, precio: bruto / (1 + newIva/100) };
        }
        return { ...l, iva: newIva };
      }
      if (f === 'precio') {
        if (v === '' || v === '-') return { ...l, precio: v };
        const num = parseFloat(v);
        if (isNaN(num)) return l;
        return { ...l, precio: l.ivaIncl ? num / (1 + (l.iva||0)/100) : num };
      }
      return { ...l, [f]: f==='cant' ? parseFloat(v)||0 : v };
    });
    onChange(next);
  };
  const precioMostrado = (l) => {
    if (typeof l.precio !== 'number') return l.precio;
    return l.ivaIncl ? Math.round(l.precio * (1 + (l.iva||0)/100) * 100) / 100 : l.precio;
  };
  const del = (i) => onChange(lineas.filter((_, idx) => idx !== i));

  // Busca por nombre, ref O código de barras — soporta pistola lectora
  const matchArt = (val) => matchArticuloPorCodigo(articulos, val);

  const elegirArticulo = (i, a) => {
    onChange(lineas.map((l, idx) => idx === i ? { ...l, desc: a.nombre, precio: a.precio, iva: a.iva } : l));
    setBuscadorIdx(null);
    setBuscadorQ('');
  };

  const sugerenciasBuscador = buscadorQ.trim() ? (() => {
    const q = normalizarBusqueda(buscadorQ);
    return (articulos || []).filter(a =>
      normalizarBusqueda(a.nombre).includes(q) || normalizarBusqueda(a.ref).includes(q) || normalizarBusqueda(a.barcode).includes(q)
    ).slice(0, 8);
  })() : (articulos || []).slice(0, 8);

  // Soporte pistola lectora: detecta que se ha escaneado un código (termina en Enter rápido)
  const handleKeyDown = (i, e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const ahora = Date.now();
      if (ahora - ultimoEscaneo.current < BLOQUEO_ESCANEO_MS) return; // ignora doble lectura mientras está "bloqueado"
      const val = e.target.value.trim();
      const a = matchArt(val);
      if (a) {
        // Artículo encontrado por barcode: rellenar y mover foco a cantidad
        onChange(lineas.map((l, idx) => idx === i ? { ...l, desc: a.nombre, precio: a.precio, iva: a.iva } : l));
        ultimoEscaneo.current = ahora;
        setFilaEscaneada(i);
        setTimeout(() => setFilaEscaneada(f => f === i ? null : f), BLOQUEO_ESCANEO_MS);
        // Foco a la celda de cantidad de esta fila
        const row = e.target.closest('tr');
        if (row) { const cantInput = row.querySelectorAll('input')[1]; if (cantInput) cantInput.focus(); }
      }
    }
  };

  return (
    <div>
      <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
        <thead>
          <tr style={{ background:t.surface2 }}>
            {['Descripción / Cód.barras','Cant.','Precio u.','IVA incl.','IVA%','Total',''].map(h => (
              <th key={h} style={{ padding:'6px 8px', textAlign:'left', color:t.muted, fontWeight:600, fontSize:11, borderBottom:`1px solid ${t.border}` }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {lineas.map((l, i) => (
            <tr key={i}>
              <td style={{ padding:'4px 4px', position:'relative' }}>
                <div style={{ display:'flex', gap:4 }}>
                  <input value={l.desc}
                    onChange={e => upd(i,'desc',e.target.value)}
                    onKeyDown={e => handleKeyDown(i, e)}
                    readOnly={filaEscaneada === i}
                    style={{ flex:1, width:'100%', padding:'5px 8px', border:`1px solid ${t.borderStrong}`, borderRadius:6, fontSize:12, outline:'none', background: filaEscaneada === i ? t.surface2 : undefined }}
                    placeholder="Descripción o escanea código..." />
                  {filaEscaneada === i && <span title="Leído, espera un momento..." style={{ display:'flex', alignItems:'center', fontSize:14 }}>⏳</span>}
                  <button type="button" onClick={() => { setBuscadorIdx(buscadorIdx === i ? null : i); setBuscadorQ(''); }}
                    title="Buscar artículo"
                    style={{ flexShrink:0, background:'none', border:`1px solid ${t.borderStrong}`, borderRadius:6, cursor:'pointer', fontSize:13, padding:'0 8px', color:t.muted }}>
                    🔍
                  </button>
                </div>
                {buscadorIdx === i && (
                  <div style={{ position:'absolute', zIndex:20, top:'100%', left:0, right:0, marginTop:2, background:t.surface, border:`1px solid ${t.border}`, borderRadius:8, boxShadow:'0 4px 14px rgba(0,0,0,0.15)', maxHeight:220, overflowY:'auto' }}>
                    <div style={{ position:'relative' }}>
                      <input autoFocus value={buscadorQ} onChange={e=>setBuscadorQ(e.target.value)}
                        placeholder="Buscar por nombre, ref. o código..."
                        style={{ width:'100%', padding:'8px 32px 8px 10px', border:'none', borderBottom:`1px solid ${t.border}`, fontSize:12, outline:'none', boxSizing:'border-box' }} />
                      {buscadorQ ? <BotonLimpiar onClick={() => setBuscadorQ('')} /> : null}
                    </div>
                    {sugerenciasBuscador.map(a => (
                      <div key={a.id} onClick={() => elegirArticulo(i, a)}
                        style={{ padding:'7px 10px', cursor:'pointer', fontSize:12, borderBottom:`1px solid ${t.border}` }}
                        onMouseDown={e => e.preventDefault()}>
                        <div style={{ fontWeight:600, color:t.body }}>{a.nombre}</div>
                        <div style={{ color:t.faint, fontSize:11 }}>{a.ref}{a.barcode ? ` · ${a.barcode}` : ''}</div>
                      </div>
                    ))}
                    {!sugerenciasBuscador.length && <div style={{ padding:10, fontSize:12, color:t.faint }}>Sin resultados</div>}
                    <div style={{ padding:'4px 10px', textAlign:'right' }}>
                      <button type="button" onClick={() => setBuscadorIdx(null)} style={{ background:'none', border:'none', color:t.faint, fontSize:11, cursor:'pointer' }}>Cerrar</button>
                    </div>
                  </div>
                )}
              </td>
              <td style={{ padding:'4px 4px' }}><input type="number" min="0" step="1" value={l.cant} onChange={e=>upd(i,'cant',e.target.value)} style={{ width:60, padding:'5px 8px', border:`1px solid ${t.borderStrong}`, borderRadius:6, fontSize:12, outline:'none' }} /></td>
              <td style={{ padding:'4px 4px' }}><input type="number" step="0.01" className="no-spin" value={precioMostrado(l)} onChange={e=>upd(i,'precio',e.target.value)} style={{ width:80, padding:'5px 8px', border:`1px solid ${t.borderStrong}`, borderRadius:6, fontSize:12, outline:'none' }} /></td>
              <td style={{ padding:'4px 4px', textAlign:'center' }}>
                <input type="checkbox" checked={!!l.ivaIncl} onChange={e=>upd(i,'ivaIncl',e.target.checked)} title="El precio introducido ya incluye el IVA" />
              </td>
              <td style={{ padding:'4px 4px' }}>
                <select value={l.iva} onChange={e=>upd(i,'iva',e.target.value)} style={{ padding:'5px 6px', border:`1px solid ${t.borderStrong}`, borderRadius:6, fontSize:12, outline:'none' }}>
                  {[0,4,10,21].map(v=><option key={v} value={v}>{v}%</option>)}
                </select>
              </td>
              <td style={{ padding:'4px 8px', fontWeight:600, color:'#1f2937', whiteSpace:'nowrap' }}>{formatEur(l.cant*l.precio)}</td>
              <td style={{ padding:'4px 4px' }}><button onClick={()=>del(i)} style={{ background:'none', border:'none', color:'#ef4444', cursor:'pointer', fontSize:16, padding:'2px 4px' }}>✕</button></td>
            </tr>
          ))}
        </tbody>
      </table>
      <button onClick={add} style={{ marginTop:8, background:'none', border:'1px dashed #d1d5db', borderRadius:8, padding:'5px 12px', fontSize:12, color:t.muted, cursor:'pointer', display:'flex', alignItems:'center', gap:4 }}>
        + Añadir línea
      </button>
    </div>
  );
}

export function StatCard({ label, value, color }) {
  const t = useT();
  return (
    <div style={{ background:t.surface2, borderRadius:10, padding:'12px 14px', border:`1px solid ${t.border}` }}>
      <div style={{ fontSize:11, color:t.faint, fontWeight:600, textTransform:'uppercase', letterSpacing:'.04em', marginBottom:4 }}>{label}</div>
      <div style={{ fontSize:22, fontWeight:700, color: color || t.primary }}>{value}</div>
    </div>
  );
}

// ✕ para vaciar un buscador (sin quitarle el foco al input).
export function BotonLimpiar({ onClick }) {
  const t = useT();
  return (
    <button type="button" title="Borrar búsqueda" aria-label="Borrar búsqueda"
      onMouseDown={e => e.preventDefault()} onClick={onClick}
      style={{ position:'absolute', right:7, top:'50%', transform:'translateY(-50%)', width:20, height:20, padding:0, borderRadius:'50%', border:'none', background:t.surface2, color:t.muted, cursor:'pointer', fontSize:11, lineHeight:1, display:'flex', alignItems:'center', justifyContent:'center' }}>✕</button>
  );
}

export function SearchBar({ value, onChange, placeholder='Buscar...', children }) {
  const t = useT();
  return (
    <div style={{ display:'flex', gap:8, marginBottom:16, alignItems:'center' }}>
      <div style={{ flex:1, position:'relative' }}>
        <span style={{ position:'absolute', left:10, top:'50%', transform:'translateY(-50%)', color:t.faint, fontSize:14 }}>🔍</span>
        <input value={value} onChange={e=>onChange(e.target.value)} placeholder={placeholder}
          style={{ width:'100%', padding:'8px 32px 8px 32px', border:`1px solid ${t.borderStrong}`, borderRadius:8, fontSize:13, outline:'none', background:t.surface, color:t.ink }} />
        {value ? <BotonLimpiar onClick={() => onChange('')} /> : null}
      </div>
      {children}
    </div>
  );
}

export function Confirm({ msg, onOk, onCancel }) {
  const t = useT();
  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.45)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:2000 }}>
      <div style={{ background:t.surface, borderRadius:12, padding:24, width:340, boxShadow:'0 20px 60px rgba(0,0,0,.4)' }}>
        <div style={{ fontSize:14, color:t.body, marginBottom:20 }}>{msg}</div>
        <div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
          <Btn variant="outline" onClick={onCancel}>Cancelar</Btn>
          <Btn variant="danger" onClick={onOk}>Eliminar</Btn>
        </div>
      </div>
    </div>
  );
}

export function useLocalStorage(key, defaultValue) {
  const [value, setValue] = useState(() => {
    try { const s = localStorage.getItem(key); return s !== null ? JSON.parse(s) : defaultValue; }
    catch { return defaultValue; }
  });
  const set = (v) => { setValue(v); try { localStorage.setItem(key, JSON.stringify(v)); } catch {} };
  return [value, set];
}

// Compara 2 códigos de barras tolerando espacios y la equivalencia EAN-13/UPC-A
// (un EAN-13 que empieza por 0 es el mismo código que su UPC-A de 12 dígitos sin ese 0)
function barcodesEquivalen(a, b) {
  if (!a || !b) return false;
  if (a === b) return true;
  if (/^\d+$/.test(a) && /^\d+$/.test(b)) {
    if (a.length === b.length + 1 && a[0] === '0' && a.slice(1) === b) return true;
    if (b.length === a.length + 1 && b[0] === '0' && b.slice(1) === a) return true;
  }
  return false;
}

// Busca un artículo por nombre exacto, referencia o código de barras (tolerante, soporta pistola lectora)
export function matchArticuloPorCodigo(articulos, val) {
  const v = String(val ?? '').trim();
  if (!v) return null;
  return articulos?.find(x =>
    x.nombre === v || x.ref === v || barcodesEquivalen(String(x.barcode ?? '').trim(), v)
  );
}

function normalizarBusqueda(s) {
  return String(s ?? '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
}

export function useSearch(items, fields, storageKey = null) {
  const [q, setQState] = useState(() => {
    if (!storageKey) return '';
    try { return localStorage.getItem('search_' + storageKey) || ''; } catch { return ''; }
  });
  const setQ = (v) => {
    setQState(v);
    if (storageKey) try { localStorage.setItem('search_' + storageKey, v); } catch {}
  };
  const filtered = useMemo(() => (
    q.trim() ? (() => {
      const tokens = normalizarBusqueda(q).split(/\s+/).filter(Boolean);
      return items.filter(item => {
        const haystack = fields.map(f => normalizarBusqueda(item[f])).join(' ');
        return tokens.every(tok => haystack.includes(tok));
      });
    })() : items
  // eslint-disable-next-line react-hooks/exhaustive-deps -- fields es un array literal fijo en cada call site, no cambia semánticamente
  ), [items, q]);
  return { q, setQ, filtered };
}

/* ══════════════════════════════════════════════════════
   ClienteSelector — selector con búsqueda, botón nuevo
   cliente inline y autoguardado si no existe
══════════════════════════════════════════════════════ */
// Formulario completo de alta/edición de cliente, compartido por los modos
// 'nuevo' y 'editar' de ClienteSelector — mismos campos en los dos casos,
// para poder rellenar el NIF antes de facturar sin salir del documento en curso.
function ClienteFormCard({ inicial, nombreInicial, titulo, clientes, onElegir, onGuardar, onCancelar }) {
  const t = useT();
  const [nombre, setNombre]         = useState(inicial?.nombre || nombreInicial || '');
  const [nif, setNif]               = useState(inicial?.nif || '');
  const [tel, setTel]               = useState(inicial?.telefono || '');
  const [email, setEmail]           = useState(inicial?.email || '');
  const [calle, setCalle]           = useState(inicial?.calle || '');
  const [cp, setCp]                 = useState(inicial?.cp || '');
  const [localidad, setLocalidad]   = useState(inicial?.localidad || '');
  const [provincia, setProvincia]   = useState(inicial?.provincia || '');
  const norm = x => (x||'').toString().normalize('NFD').replace(/[̀-ͯ]/g,'').toLowerCase();
  const qn = norm(nombre.trim());
  const coincidencias = !inicial && qn.length >= 2
    ? clientes.filter(c => norm(c.nombre).includes(qn) || norm(c.nif).includes(qn) || norm(c.telefono).includes(qn)).slice(0, 6)
    : [];

  function guardar() {
    if (!nombre.trim()) return alert('El nombre es obligatorio.');
    const direccion = [calle, cp, localidad, provincia].filter(Boolean).map(s=>s.trim()).join(', ');
    onGuardar({
      id: inicial?.id ?? Date.now(),
      nombre: nombre.trim(), nif: nif.trim(), telefono: tel.trim(), email: email.trim(),
      calle: calle.trim(), cp: cp.trim(), localidad: localidad.trim(), provincia: provincia.trim(),
      direccion,
    });
  }

  return (
    <div style={{ border:`1px solid ${t.primary}`, borderRadius:10, padding:'12px 14px', background:t.primaryBg, gridColumn:'1/-1' }}>
      <div style={{ fontSize:12, fontWeight:700, color:t.primary, marginBottom:10, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
        <span>{titulo}</span>
        <button onClick={onCancelar} style={{ background:'none', border:'none', color:t.faint, cursor:'pointer', fontSize:16 }}>×</button>
      </div>
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8, marginBottom:10 }}>
        <div style={{ gridColumn:'1/-1' }}>
          <label style={{ fontSize:11, fontWeight:600, color:t.muted, textTransform:'uppercase', letterSpacing:'.04em', display:'block', marginBottom:3 }}>Nombre / Empresa *</label>
          <input autoFocus value={nombre} onChange={e=>setNombre(e.target.value)}
            placeholder="Nombre completo o razón social"
            style={{ width:'100%', padding:'7px 10px', border:`1px solid ${t.borderStrong}`, borderRadius:8, fontSize:13, outline:'none' }} />
          {coincidencias.length > 0 && (
            <div style={{ marginTop:6, border:`1px solid ${t.borderStrong}`, borderRadius:8, background:t.surface, overflow:'hidden' }}>
              <div style={{ fontSize:11, color:t.muted, padding:'5px 10px', background:t.surface2 }}>Ya existe en la base de datos, pulsa para elegirlo:</div>
              {coincidencias.map(c => (
                <button key={c.id} type="button" onClick={() => onElegir(c)}
                  style={{ display:'flex', flexDirection:'column', gap:1, width:'100%', textAlign:'left', padding:'7px 10px', border:'none', borderTop:`1px solid ${t.borderStrong}`, background:t.surface, cursor:'pointer' }}>
                  <span style={{ fontSize:13, fontWeight:600, color:t.ink }}>{c.nombre}</span>
                  <span style={{ fontSize:11, color:t.muted }}>{[c.nif, c.telefono].filter(Boolean).join(' · ') || '—'}</span>
                </button>
              ))}
            </div>
          )}
        </div>
        <div>
          <label style={{ fontSize:11, fontWeight:600, color:t.muted, textTransform:'uppercase', letterSpacing:'.04em', display:'block', marginBottom:3 }}>NIF / CIF</label>
          <input value={nif} onChange={e=>setNif(e.target.value)} placeholder="12345678A"
            style={{ width:'100%', padding:'7px 10px', border:`1px solid ${t.borderStrong}`, borderRadius:8, fontSize:13, outline:'none' }} />
        </div>
        <div>
          <label style={{ fontSize:11, fontWeight:600, color:t.muted, textTransform:'uppercase', letterSpacing:'.04em', display:'block', marginBottom:3 }}>Teléfono</label>
          <input value={tel} onChange={e=>setTel(e.target.value)} placeholder="600 000 000"
            style={{ width:'100%', padding:'7px 10px', border:`1px solid ${t.borderStrong}`, borderRadius:8, fontSize:13, outline:'none' }} />
        </div>
        <div>
          <label style={{ fontSize:11, fontWeight:600, color:t.muted, textTransform:'uppercase', letterSpacing:'.04em', display:'block', marginBottom:3 }}>Email</label>
          <input value={email} onChange={e=>setEmail(e.target.value)} type="email" placeholder="correo@ejemplo.com"
            style={{ width:'100%', padding:'7px 10px', border:`1px solid ${t.borderStrong}`, borderRadius:8, fontSize:13, outline:'none' }} />
        </div>
        <div style={{ gridColumn:'1/-1' }}>
          <label style={{ fontSize:11, fontWeight:600, color:t.muted, textTransform:'uppercase', letterSpacing:'.04em', display:'block', marginBottom:3 }}>Calle</label>
          <input value={calle} onChange={e=>setCalle(e.target.value)} placeholder="C/ Rodriguez San Pedro 31"
            style={{ width:'100%', padding:'7px 10px', border:`1px solid ${t.borderStrong}`, borderRadius:8, fontSize:13, outline:'none' }} />
        </div>
        <div>
          <label style={{ fontSize:11, fontWeight:600, color:t.muted, textTransform:'uppercase', letterSpacing:'.04em', display:'block', marginBottom:3 }}>Cód. postal</label>
          <input value={cp} onChange={e=>setCp(e.target.value)} placeholder="28015"
            style={{ width:'100%', padding:'7px 10px', border:`1px solid ${t.borderStrong}`, borderRadius:8, fontSize:13, outline:'none' }} />
        </div>
        <div>
          <label style={{ fontSize:11, fontWeight:600, color:t.muted, textTransform:'uppercase', letterSpacing:'.04em', display:'block', marginBottom:3 }}>Localidad</label>
          <input value={localidad} onChange={e=>setLocalidad(e.target.value)} placeholder="Madrid"
            style={{ width:'100%', padding:'7px 10px', border:`1px solid ${t.borderStrong}`, borderRadius:8, fontSize:13, outline:'none' }} />
        </div>
        <div style={{ gridColumn:'1/-1' }}>
          <label style={{ fontSize:11, fontWeight:600, color:t.muted, textTransform:'uppercase', letterSpacing:'.04em', display:'block', marginBottom:3 }}>Provincia</label>
          <input value={provincia} onChange={e=>setProvincia(e.target.value)} placeholder="Madrid"
            style={{ width:'100%', padding:'7px 10px', border:`1px solid ${t.borderStrong}`, borderRadius:8, fontSize:13, outline:'none' }} />
        </div>
      </div>
      <div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
        <button onClick={onCancelar} style={{ padding:'6px 14px', borderRadius:8, border:`1px solid ${t.borderStrong}`, background:t.surface, color:t.ink, fontSize:13, cursor:'pointer' }}>Cancelar</button>
        <button onClick={guardar} style={{ padding:'6px 14px', borderRadius:8, border:'none', background:t.primary, color: t===THEME.dark ? '#0b0b09' : '#fff', fontSize:13, fontWeight:600, cursor:'pointer' }}>✓ Guardar cliente</button>
      </div>
    </div>
  );
}

export function ClienteSelector({ clientes, clienteId, clienteNombre, onChange, onNuevoCliente, onEditarCliente }) {
  const t = useT();
  const [modo, setModo] = useState('select'); // 'select' | 'nuevo' | 'editar' | 'buscar'
  const [busq, setBusq] = useState('');
  const [prefillNuevo, setPrefillNuevo] = useState('');

  function handleSelect(id) {
    if (!id) { onChange({ id:'', nombre:'', nif:'', direccion:'', telefono:'', email:'' }); return; }
    const c = clientes.find(x => String(x.id) === String(id));
    if (c) onChange({ id: c.id, nombre: c.nombre, nif: c.nif || '', direccion: c.direccion || '', telefono: c.telefono || '', email: c.email || '' });
  }

  // Normalize clienteId to string for select comparison
  const selectedValue = clienteId ? String(clienteId) : '';

  if (modo === 'nuevo' || modo === 'editar') {
    const inicial = modo === 'editar' ? clientes.find(c => String(c.id) === selectedValue) : null;
    return (
      <ClienteFormCard
        inicial={inicial}
        nombreInicial={modo === 'nuevo' ? prefillNuevo : ''}
        titulo={modo === 'editar' ? '✏️ Editar cliente' : '➕ Nuevo cliente'}
        clientes={clientes}
        onElegir={c => { handleSelect(c.id); setModo('select'); }}
        onCancelar={() => setModo('select')}
        onGuardar={cliente => {
          if (modo === 'editar') onEditarCliente(cliente); else onNuevoCliente(cliente);
          onChange({ id: cliente.id, nombre: cliente.nombre, nif: cliente.nif, direccion: cliente.direccion, telefono: cliente.telefono || '', email: cliente.email || '' });
          setModo('select');
        }}
      />
    );
  }

  if (modo === 'buscar') {
    const norm = s => (s||'').toString().normalize('NFD').replace(/[̀-ͯ]/g,'').toLowerCase();
    const qn = norm(busq);
    const resultados = qn ? clientes.filter(c => norm(c.nombre).includes(qn) || norm(c.nif).includes(qn) || norm(c.telefono).includes(qn)).slice(0,30) : [];
    return (
      <div style={{ border:`1px solid ${t.primary}`, borderRadius:10, padding:'12px 14px', background:t.primaryBg, gridColumn:'1/-1' }}>
        <div style={{ fontSize:12, fontWeight:700, color:t.primary, marginBottom:10, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <span>🔍 Buscar cliente</span>
          <button onClick={() => { setModo('select'); setBusq(''); }} style={{ background:'none', border:'none', color:t.faint, cursor:'pointer', fontSize:16 }}>×</button>
        </div>
        <div style={{ position:'relative', marginBottom:8 }}>
          <input autoFocus value={busq} onChange={e=>setBusq(e.target.value)}
            placeholder="Nombre, NIF o teléfono..."
            style={{ width:'100%', padding:'8px 32px 8px 10px', border:`1px solid ${t.borderStrong}`, borderRadius:8, fontSize:13, outline:'none', boxSizing:'border-box' }} />
          {busq ? <BotonLimpiar onClick={() => setBusq('')} /> : null}
        </div>
        <div style={{ maxHeight:220, overflowY:'auto', display:'flex', flexDirection:'column', gap:2 }}>
          {qn && resultados.length === 0 && (
            <>
              <div style={{ fontSize:12, color:t.muted, padding:'8px 4px 4px' }}>Sin resultados.</div>
              <button type="button" onClick={() => { setPrefillNuevo(busq.trim()); setModo('nuevo'); setBusq(''); }}
                style={{ textAlign:'left', padding:'9px 10px', borderRadius:8, border:`1.5px dashed ${t.primary}`, background:'none', color:t.primary, fontWeight:700, fontSize:13, cursor:'pointer' }}>
                ➕ Añadir "{busq.trim()}" como nuevo cliente
              </button>
            </>
          )}
          {resultados.map(c => (
            <button key={c.id} type="button" onClick={() => { handleSelect(c.id); setModo('select'); setBusq(''); }}
              style={{ textAlign:'left', padding:'8px 10px', borderRadius:8, border:'none', background:t.surface, cursor:'pointer', display:'flex', flexDirection:'column', gap:1 }}>
              <span style={{ fontSize:13, fontWeight:600, color:t.ink }}>{c.nombre}</span>
              <span style={{ fontSize:11, color:t.muted }}>{[c.nif, c.telefono].filter(Boolean).join(' · ') || '—'}</span>
            </button>
          ))}
        </div>
      </div>
    );
  }

  // Modo select normal
  const yaExiste = clienteNombre && clientes.some(c => c.nombre.toLowerCase() === clienteNombre.toLowerCase());
  const nombreLibre = clienteNombre && !selectedValue && !yaExiste;

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
      <label style={{ fontSize:11, fontWeight:600, color:t.muted, textTransform:'uppercase', letterSpacing:'.04em' }}>Cliente</label>
      <div style={{ display:'flex', gap:6, alignItems:'stretch' }}>
        <div style={{ flex:1, position:'relative' }}>
          <select value={selectedValue} onChange={e => handleSelect(e.target.value)}
            style={{ width:'100%', padding:'7px 10px', border:`1px solid ${t.borderStrong}`, borderRadius:8, fontSize:13, color:t.ink, background:t.surface, outline:'none', height:'100%' }}>
            <option value="">— Sin cliente / Particular —</option>
            {[...clientes].sort((a,b) => a.nombre.localeCompare(b.nombre, 'es')).map(c => <option key={c.id} value={String(c.id)}>{c.nombre}{c.nif ? ` (${c.nif})` : ''}</option>)}
          </select>
        </div>
        {selectedValue && onEditarCliente && (
          <button onClick={() => setModo('editar')} title="Editar este cliente"
            style={{ padding:'7px 12px', borderRadius:8, border:`1px solid ${t.borderStrong}`, background:t.surface, color:t.ink, fontSize:13, cursor:'pointer', whiteSpace:'nowrap', flexShrink:0 }}>
            ✏️ Editar
          </button>
        )}
        <button onClick={() => setModo('buscar')} title="Buscar cliente"
          style={{ padding:'7px 12px', borderRadius:8, border:`1px solid ${t.borderStrong}`, background:t.surface, color:t.ink, fontSize:13, cursor:'pointer', whiteSpace:'nowrap', flexShrink:0 }}>
          🔍 Buscar
        </button>
        <button onClick={() => setModo('nuevo')} title="Añadir nuevo cliente"
          style={{ padding:'7px 12px', borderRadius:8, border:`1px solid ${t.primary}`, background:t.primaryBg, color:t.primary, fontSize:13, fontWeight:700, cursor:'pointer', whiteSpace:'nowrap', flexShrink:0 }}>
          + Nuevo
        </button>
      </div>
      {nombreLibre && (
        <div style={{ fontSize:11, color: t===THEME.dark ? '#fbbf24' : '#c27803', background: t===THEME.dark ? 'rgba(245,158,11,0.14)' : '#fef3c7', borderRadius:6, padding:'4px 8px', display:'flex', alignItems:'center', gap:4 }}>
          ⚠️ «{clienteNombre}» no está en la lista — se guardará como nuevo cliente al confirmar
        </div>
      )}
      {selectedValue && (
        <div style={{ fontSize:11, color: t===THEME.dark ? '#6ee7b7' : '#0e9f6e', display:'flex', alignItems:'center', gap:4 }}>
          ✓ Cliente seleccionado de la lista
        </div>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════
   ProveedorSelector — selector con búsqueda, botón nuevo
   proveedor inline y autoguardado si no existe
══════════════════════════════════════════════════════ */
export function ProveedorSelector({ proveedores, proveedorId, proveedorNombre, onChange, onNuevoProveedor }) {
  const t = useT();
  const [modo, setModo] = useState('select');
  const [nuevoNombre, setNuevoNombre] = useState('');
  const [nuevoCif,    setNuevoCif]    = useState('');
  const [nuevoTel,    setNuevoTel]    = useState('');
  const [nuevoEmail,  setNuevoEmail]  = useState('');

  function guardarNuevo() {
    if (!nuevoNombre.trim()) return alert('El nombre es obligatorio.');
    const nuevo = { id: Date.now(), nombre: nuevoNombre.trim(), cif: nuevoCif.trim(), telefono: nuevoTel.trim(), email: nuevoEmail.trim(), contacto:'', web:'', notas:'' };
    onNuevoProveedor(nuevo);
    onChange({ id: nuevo.id, nombre: nuevo.nombre });
    setModo('select');
    setNuevoNombre(''); setNuevoCif(''); setNuevoTel(''); setNuevoEmail('');
  }

  function cancelarNuevo() {
    setModo('select');
    setNuevoNombre(''); setNuevoCif(''); setNuevoTel(''); setNuevoEmail('');
  }

  function handleSelect(id) {
    if (!id) { onChange({ id:'', nombre:'' }); return; }
    const p = proveedores.find(x => String(x.id) === String(id));
    if (p) onChange({ id: p.id, nombre: p.nombre });
  }

  const selectedValue = proveedorId ? String(proveedorId) : '';

  if (modo === 'nuevo') {
    return (
      <div style={{ border:`1px solid ${t.primary}`, borderRadius:10, padding:'12px 14px', background:t.primaryBg }}>
        <div style={{ fontSize:12, fontWeight:700, color:t.primary, marginBottom:10, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <span>➕ Nuevo proveedor</span>
          <button onClick={cancelarNuevo} style={{ background:'none', border:'none', color:t.faint, cursor:'pointer', fontSize:16 }}>×</button>
        </div>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8, marginBottom:10 }}>
          <div style={{ gridColumn:'1/-1' }}>
            <label style={{ fontSize:11, fontWeight:600, color:t.muted, textTransform:'uppercase', letterSpacing:'.04em', display:'block', marginBottom:3 }}>Nombre / Empresa *</label>
            <input autoFocus value={nuevoNombre} onChange={e=>setNuevoNombre(e.target.value)}
              placeholder="Razón social del proveedor"
              style={{ width:'100%', padding:'7px 10px', border:`1px solid ${t.borderStrong}`, borderRadius:8, fontSize:13, outline:'none' }} />
          </div>
          <div>
            <label style={{ fontSize:11, fontWeight:600, color:t.muted, textTransform:'uppercase', letterSpacing:'.04em', display:'block', marginBottom:3 }}>CIF</label>
            <input value={nuevoCif} onChange={e=>setNuevoCif(e.target.value)} placeholder="B12345678"
              style={{ width:'100%', padding:'7px 10px', border:`1px solid ${t.borderStrong}`, borderRadius:8, fontSize:13, outline:'none' }} />
          </div>
          <div>
            <label style={{ fontSize:11, fontWeight:600, color:t.muted, textTransform:'uppercase', letterSpacing:'.04em', display:'block', marginBottom:3 }}>Teléfono</label>
            <input value={nuevoTel} onChange={e=>setNuevoTel(e.target.value)} placeholder="900 000 000"
              style={{ width:'100%', padding:'7px 10px', border:`1px solid ${t.borderStrong}`, borderRadius:8, fontSize:13, outline:'none' }} />
          </div>
          <div>
            <label style={{ fontSize:11, fontWeight:600, color:t.muted, textTransform:'uppercase', letterSpacing:'.04em', display:'block', marginBottom:3 }}>Email</label>
            <input value={nuevoEmail} onChange={e=>setNuevoEmail(e.target.value)} placeholder="contacto@proveedor.com"
              style={{ width:'100%', padding:'7px 10px', border:`1px solid ${t.borderStrong}`, borderRadius:8, fontSize:13, outline:'none' }} />
          </div>
        </div>
        <div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
          <button onClick={cancelarNuevo} style={{ padding:'6px 14px', borderRadius:8, border:`1px solid ${t.borderStrong}`, background:t.surface, color:t.ink, fontSize:13, cursor:'pointer' }}>Cancelar</button>
          <button onClick={guardarNuevo} style={{ padding:'6px 14px', borderRadius:8, border:'none', background:t.primary, color: t===THEME.dark ? '#0b0b09' : '#fff', fontSize:13, fontWeight:600, cursor:'pointer' }}>Guardar proveedor</button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
      <label style={{ fontSize:11, fontWeight:600, color:t.muted, textTransform:'uppercase', letterSpacing:'.04em' }}>Proveedor</label>
      <div style={{ display:'flex', gap:6, alignItems:'stretch' }}>
        <div style={{ flex:1 }}>
          <select value={selectedValue} onChange={e => handleSelect(e.target.value)}
            style={{ width:'100%', padding:'7px 10px', border:`1px solid ${t.borderStrong}`, borderRadius:8, fontSize:13, color:t.ink, background:t.surface, outline:'none' }}>
            <option value="">— Seleccionar proveedor —</option>
            {proveedores.map(p => <option key={p.id} value={String(p.id)}>{p.nombre}{p.cif ? ` (${p.cif})` : ''}</option>)}
          </select>
        </div>
        <button onClick={() => setModo('nuevo')} title="Añadir nuevo proveedor"
          style={{ padding:'7px 12px', borderRadius:8, border:`1px solid ${t.primary}`, background:t.primaryBg, color:t.primary, fontSize:13, fontWeight:700, cursor:'pointer', whiteSpace:'nowrap', flexShrink:0 }}>
          + Nuevo
        </button>
      </div>
      {selectedValue && (
        <div style={{ fontSize:11, color: t===THEME.dark ? '#6ee7b7' : '#0e9f6e', display:'flex', alignItems:'center', gap:4 }}>
          ✓ Proveedor seleccionado de la lista
        </div>
      )}
    </div>
  );
}

export function MultiSelect({ options, selected, onChange, placeholder='Todos' }) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    function h(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);
  const toggle = v => onChange(selected.includes(v) ? selected.filter(s => s !== v) : [...selected, v]);
  const label = selected.length === 0 ? placeholder : selected.length === 1 ? selected[0] : `${selected.length} seleccionados`;
  return (
    <div ref={ref} style={{ position:'relative', display:'inline-block', minWidth:160 }}>
      <button onClick={() => setOpen(o => !o)} style={{ padding:'7px 12px', border:`1px solid ${t.borderStrong}`, borderRadius:8, background:t.surface, fontSize:13, cursor:'pointer', display:'flex', alignItems:'center', gap:6, color: selected.length ? t.ink : t.faint, fontFamily:'inherit' }}>
        <span style={{ flex:1, textAlign:'left' }}>{label}</span>
        <span style={{ fontSize:10, color:t.faint }}>▼</span>
      </button>
      {open && (
        <div style={{ position:'absolute', top:'calc(100% + 4px)', left:0, zIndex:200, background:t.surface, border:`1px solid ${t.border}`, borderRadius:10, boxShadow:'0 8px 24px rgba(0,0,0,.2)', minWidth:210, padding:'6px 0' }}>
          <div onClick={() => onChange([])} style={{ padding:'7px 14px', fontSize:13, cursor:'pointer', display:'flex', alignItems:'center', gap:8, background: selected.length===0 ? t.primaryBg : 'transparent', color: selected.length===0 ? t.primary : t.body, fontWeight: selected.length===0 ? 700 : 400 }}>
            <span>{selected.length===0 ? '☑' : '☐'}</span> Todos
          </div>
          <div style={{ height:1, background:t.border, margin:'4px 0' }} />
          {options.map(opt => {
            const sel = selected.includes(opt);
            return (
              <div key={opt} onClick={() => toggle(opt)} style={{ padding:'7px 14px', fontSize:13, cursor:'pointer', display:'flex', alignItems:'center', gap:8, background: sel ? t.primaryBg : 'transparent', color: sel ? t.primary : t.body, fontWeight: sel ? 700 : 400 }}>
                <span>{sel ? '☑' : '☐'}</span> {opt}
              </div>
            );
          })}
          {selected.length > 0 && (
            <><div style={{ height:1, background:t.border, margin:'4px 0' }} />
            <div onClick={() => onChange([])} style={{ padding:'5px 14px', fontSize:11, cursor:'pointer', color:t.faint, textAlign:'center' }}>Limpiar filtro</div></>
          )}
        </div>
      )}
    </div>
  );
}

export function EstadoQuick({ estado, estados, colores = {}, textos = {}, onChange }) {
  const t = useT();
  const [pos, setPos] = useState(null);
  const ref = useRef(null);

  useEffect(() => {
    if (!pos) return;
    const h = () => setPos(null);
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [pos]);

  const abrir = e => {
    e.stopPropagation();
    const rect = ref.current.getBoundingClientRect();
    setPos({ top: rect.bottom + 4, left: rect.left });
  };

  const bg   = colores[estado] || t.surface2;
  const color = textos[estado]  || t.body;

  return (
    <>
      <span ref={ref} onClick={abrir}
        style={{ display:'inline-block', padding:'3px 9px', borderRadius:20, fontSize:11, fontWeight:700, background:bg, color, cursor:'pointer', userSelect:'none', whiteSpace:'nowrap' }}
        title="Cambiar estado">
        {estado} ▾
      </span>
      {pos && (
        <div onMouseDown={e => e.stopPropagation()}
          style={{ position:'fixed', top:pos.top, left:pos.left, zIndex:9999, background:t.surface, border:`1px solid ${t.border}`, borderRadius:10, boxShadow:'0 4px 20px rgba(0,0,0,.25)', minWidth:200, overflow:'hidden' }}>
          {estados.map(e => (
            <div key={e} onClick={() => { setPos(null); if (e !== estado) onChange(e); }}
              style={{ padding:'9px 14px', fontSize:13, fontWeight:e===estado?700:400, background:e===estado?(colores[e]||t.surface2):t.surface, color:e===estado?(textos[e]||t.ink):t.body, cursor:'pointer', borderBottom:`1px solid ${t.border}` }}
              onMouseEnter={ev => { if(e!==estado) ev.currentTarget.style.background=t.surface2; }}
              onMouseLeave={ev => { if(e!==estado) ev.currentTarget.style.background=e===estado?(colores[e]||t.surface2):t.surface; }}>
              {e === estado ? '✓ ' : ''}{e}
            </div>
          ))}
        </div>
      )}
    </>
  );
}

